import { StreemshotId, StreemshotRequestId } from '@streem/domain-id';
import { invariant } from '@streem/toolbox';
import { BehaviorSubject } from 'rxjs';
export class RenderingManager {
    constructor(context) {
        this.context = context;
        this.bufferPropertiesSubject = new BehaviorSubject(undefined);
        this.bufferProperties = this.bufferPropertiesSubject.asObservable();
        this.bufferCanvas = context.compositor.buffer;
        this.cameraFrame = context.compositor.cameraFrame;
    }
    /**
     * fulfillStreemshotRequestsForWeb will create a subscription which listens for the latest Streemshot request.
     * On a valid request, it will obtain Blob data from the target canvas
     * and invoke fulfillStreemshot on the RoomSession's streemshot controller.
     * Returns a function that unsubscribes from the subscription.
     */
    fulfillStreemshotRequestsForWeb() {
        const streemshotFulfillmentSubscription = this.context.streemshotEngine
            .fulfillStreemshotRequestsForWeb({
            localUser: this.context.roomSession.user.localUser,
            latestStreemshotRequest: this.context.roomSession.streemshots.latestRequest,
            streemshots: this.context.roomSession.streemshots.responses,
            canvas: this.bufferCanvas,
        })
            .subscribe(async ([request, blob]) => {
            invariant(request.streemshotId, "request's streemshotId must be defined");
            await this.context.roomSession.streemshots.fulfillStreemshot(new StreemshotRequestId(request.id), new StreemshotId(request.streemshotId), blob, {
                width: this.bufferCanvas.width,
                height: this.bufferCanvas.height,
            });
        });
        // Return a function to unsubscribe when the call ends.
        return () => streemshotFulfillmentSubscription.unsubscribe();
    }
    renderVideoForUser() {
        const targetCanvas = createCanvasInContainer(this.context.container);
        const targetContext = targetCanvas.getContext('2d', { alpha: false });
        invariant(targetContext, 'Unable to get 2D context');
        const renderSignalSubscription = this.context.compositor.renderSignal.subscribe(() => {
            this.drawBufferToTarget(targetCanvas, targetContext);
        });
        const disconnectCompositor = this.context.compositor.connect();
        return () => {
            if (disconnectCompositor) {
                disconnectCompositor();
            }
            if (renderSignalSubscription) {
                renderSignalSubscription.unsubscribe();
            }
            targetCanvas.remove();
        };
    }
    drawBufferToTarget(target, targetContext) {
        // Resize the target canvas to match the buffer canvas
        if (target.width !== this.bufferCanvas.width ||
            target.height !== this.bufferCanvas.height) {
            target.width = this.bufferCanvas.width;
            target.height = this.bufferCanvas.height;
        }
        // Draw image from buffer canvas onto the target canvas
        targetContext.drawImage(this.bufferCanvas, 0, 0);
        const { width: targetWidth, height: targetHeight } = target;
        if (targetWidth !== this.bufferPropertiesSubject.value?.width ||
            targetHeight !== this.bufferPropertiesSubject.value?.height) {
            this.bufferPropertiesSubject.next({
                width: targetWidth,
                height: targetHeight,
            });
        }
    }
}
function createCanvasInContainer(container) {
    const target = document.createElement('canvas');
    // fill the canvas parent conatiner while maintaining the video aspect ratio
    target.style.objectFit = 'contain';
    target.style.height = '100%';
    target.style.width = '100%';
    // do not display the context menu.
    target.addEventListener('contextmenu', event => {
        event.preventDefault();
        return false;
    });
    container.appendChild(target);
    container.addEventListener('contextmenu', event => {
        event.preventDefault();
        return false;
    });
    return target;
}
//# sourceMappingURL=rendering_manager.js.map